import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useCallback, useMemo } from 'react';

const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

interface AuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  imageUrl: string | null;
}

interface AuthState {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: AuthUser | null;
  signOut: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
}

/**
 * Dev mode auth hook - returns mock auth state
 */
function useDevAuth(): AuthState {
  return {
    isLoaded: true,
    isSignedIn: true,
    user: {
      id: 'dev-user',
      email: 'dev@localhost',
      firstName: 'Dev',
      lastName: 'User',
      fullName: 'Dev User',
      imageUrl: null,
    },
    signOut: async () => {
      console.log('[Dev Mode] Sign out called');
    },
    getAuthToken: async () => 'dev-token',
  };
}

/**
 * Production auth hook - wraps Clerk's auth hooks
 */
function useClerkAuthHook(): AuthState {
  const { isLoaded, isSignedIn, signOut, getToken } = useClerkAuth();
  const { user } = useUser();

  const handleSignOut = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!isSignedIn) return null;
    return getToken();
  }, [isSignedIn, getToken]);

  const authState = useMemo(
    () => ({
      isLoaded,
      isSignedIn: isSignedIn ?? false,
      user: user
        ? {
            id: user.id,
            email: user.primaryEmailAddress?.emailAddress ?? null,
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: user.fullName,
            imageUrl: user.imageUrl,
          }
        : null,
    }),
    [isLoaded, isSignedIn, user]
  );

  return {
    ...authState,
    signOut: handleSignOut,
    getAuthToken,
  };
}

/**
 * Custom hook for authentication state and operations
 * Wraps Clerk's auth hooks with application-specific logic
 * In dev mode, returns mock auth state without requiring ClerkProvider
 */
export const useAuth: () => AuthState = isDevMode ? useDevAuth : useClerkAuthHook;
